import { useEffect } from 'react';
import Lenis from '@studio-freight/lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const usePremiumMotion = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    gsap.registerPlugin(ScrollTrigger);

    let lenis;
    let rafId;
    const cleanupFns = [];

    if (!prefersReducedMotion) {
      lenis = new Lenis({
        duration: 1.05,
        smoothWheel: true,
        smoothTouch: false,
        wheelMultiplier: 0.95,
        touchMultiplier: 1.1,
      });

      const raf = (time) => {
        lenis.raf(time);
        rafId = window.requestAnimationFrame(raf);
      };

      rafId = window.requestAnimationFrame(raf);
      lenis.on('scroll', ScrollTrigger.update);
      // Expose so nav links can scroll smoothly through Lenis (native
      // #anchor jumps are otherwise swallowed by the smooth-scroll loop).
      window.lenis = lenis;
    }

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: document.documentElement,
        start: 'top top',
        end: 'bottom bottom',
        onUpdate: (self) => {
          document.documentElement.style.setProperty(
            '--scroll-progress',
            self.progress.toFixed(3)
          );
        },
      });

      if (prefersReducedMotion) return;

      gsap.utils.toArray('[data-reveal]').forEach((element) => {
        gsap.fromTo(
          element,
          {
            y: 36,
            opacity: 0,
            filter: 'blur(12px)',
          },
          {
            y: 0,
            opacity: 1,
            filter: 'blur(0px)',
            duration: 1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: element,
              start: 'top 88%',
              once: true,
            },
          }
        );
      });

      gsap.utils.toArray('[data-reveal-stagger]').forEach((container) => {
        const targets = container.children;
        if (!targets.length) return;

        gsap.fromTo(
          targets,
          {
            y: 28,
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: container,
              start: 'top 85%',
              once: true,
            },
          }
        );
      });

      gsap.utils.toArray('[data-parallax-speed]').forEach((element) => {
        const speed = Number(element.getAttribute('data-parallax-speed') || 0.12);
        gsap.to(element, {
          yPercent: speed * -100,
          ease: 'none',
          scrollTrigger: {
            trigger: element,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        });
      });

      gsap.utils.toArray('[data-scale-in]').forEach((element) => {
        gsap.fromTo(
          element,
          { scale: 0.92, opacity: 0.5 },
          {
            scale: 1,
            opacity: 1,
            duration: 1.2,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: element,
              start: 'top 85%',
              once: true,
            },
          }
        );
      });

      gsap.utils.toArray('[data-drift]').forEach((element, index) => {
        gsap.to(element, {
          y: index % 2 === 0 ? -10 : 10,
          x: index % 3 === 0 ? 8 : -6,
          rotation: index % 2 === 0 ? -2 : 2,
          duration: 3.8 + (index % 4) * 0.55,
          ease: 'sine.inOut',
          yoyo: true,
          repeat: -1,
        });
      });

      gsap.utils.toArray('[data-tilt]').forEach((element) => {
        const rotateXTo = gsap.quickTo(element, 'rotationX', {
          duration: 0.45,
          ease: 'power3.out',
        });
        const rotateYTo = gsap.quickTo(element, 'rotationY', {
          duration: 0.45,
          ease: 'power3.out',
        });
        const yTo = gsap.quickTo(element, 'y', {
          duration: 0.45,
          ease: 'power3.out',
        });

        gsap.set(element, {
          transformPerspective: 1200,
          transformOrigin: 'center center',
          willChange: 'transform',
        });

        const handleMove = (event) => {
          const rect = element.getBoundingClientRect();
          const offsetX = (event.clientX - rect.left) / rect.width - 0.5;
          const offsetY = (event.clientY - rect.top) / rect.height - 0.5;
          rotateYTo(offsetX * 10);
          rotateXTo(offsetY * -10);
          yTo(-4);
        };

        const handleLeave = () => {
          rotateXTo(0);
          rotateYTo(0);
          yTo(0);
        };

        element.addEventListener('mousemove', handleMove);
        element.addEventListener('mouseleave', handleLeave);
        cleanupFns.push(() => {
          element.removeEventListener('mousemove', handleMove);
          element.removeEventListener('mouseleave', handleLeave);
          gsap.set(element, { clearProps: 'transformPerspective,willChange' });
        });
      });
    });

    const refreshTimeout = window.setTimeout(() => {
      ScrollTrigger.refresh();
    }, 250);

    return () => {
      window.clearTimeout(refreshTimeout);
      cleanupFns.forEach((cleanup) => cleanup());
      ctx.revert();
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      if (rafId) window.cancelAnimationFrame(rafId);
      if (lenis) {
        lenis.destroy();
        if (window.lenis === lenis) delete window.lenis;
      }
      document.documentElement.style.removeProperty('--scroll-progress');
    };
  }, []);
};

export default usePremiumMotion;
